import {useQuery} from "@apollo/client";
import map from "lodash/map";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {
  FilterCategory,
  FilterCategoryType,
  FilterDataResults,
  FilterSets,
  MAX_GQL_RECORDS,
} from "@/components/filterDropdown/FilterDropdown.ts";
import {FilterDropdown} from "@/components/filterDropdown/FilterDropdown.tsx";
import {
  AlertFilteringDataResult,
  GQL_ALERTING_FILTER_DATA_SERVICES,
  GQL_MONITORING_GROUPS_AND_TAGS_FILTER_DATA,
} from "@/lib/queries/alerting.ts";
import {getCheckTypeOptions} from "@/lib/types/checks.ts";

type AlertFilterProps = {
  filterSets: FilterSets<FilterCategory>;
  onFilterSetChange: (filterSet: FilterSets<FilterCategory>) => void;
};

export const AlertsFilters = ({filterSets, onFilterSetChange}: AlertFilterProps) => {
  const {data, loading} = useQuery<AlertFilteringDataResult>(GQL_MONITORING_GROUPS_AND_TAGS_FILTER_DATA);

  useQuery<FilterDataResults>(GQL_ALERTING_FILTER_DATA_SERVICES, {
    variables: {search: "", limit: MAX_GQL_RECORDS},
  });

  const {user} = useCurrentUserContext();
  const checkTypeOptions = getCheckTypeOptions(user?.account?.feature_flags ? user.account.feature_flags : []);

  // Built unconditionally: only the options depend on the query, and gating the whole array on it
  // left the toggle with nothing to count, so the applied total jumped once the data landed.
  const filterFields: FilterCategory[] = [
    {
      key: "services",
      type: FilterCategoryType.GQL,
      display: "Checks",
      graphql: GQL_ALERTING_FILTER_DATA_SERVICES,
      sorted: true,
    },
    {
      key: "status",
      type: FilterCategoryType.ARRAY,
      display: "Status",
      options: [
        {
          key: "down",
          display: "Ongoing",
        },
        {
          key: "resolved",
          display: "Resolved",
        },
        {
          key: "ignored",
          display: "Ignored",
        },
      ],
    },
    {
      key: "service_monitoring_type",
      type: FilterCategoryType.ARRAY,
      display: "Check Type",
      options: map(checkTypeOptions, (value, key) => {
        return {
          key: key,
          display: value,
        };
      }),
      sorted: true,
    },
    {
      key: "created_at_range",
      type: FilterCategoryType.RANGE,
      display: "From",
    },
    {
      key: "up_at_range",
      type: FilterCategoryType.RANGE,
      display: "To",
    },
    {
      key: "locations",
      type: FilterCategoryType.ARRAY,
      display: "Locations",
      options: (data?.monitoringGroups ?? []).map((monitoringGroup) => ({
        key: monitoringGroup.id.toString(),
        display: monitoringGroup.location,
      })),
      sorted: true,
    },
    {
      key: "tags",
      type: FilterCategoryType.ARRAY,
      display: "Tags",
      options: (data?.tags ?? []).map((tag) => ({
        key: tag.id.toString(),
        display: tag.tag,
        color_hex: tag.color_hex,
      })),
      sorted: true,
      matchMode: {enabled: true},
    },
    {
      key: "contact_groups",
      type: FilterCategoryType.ARRAY,
      display: "Contact Group",
      options: (data?.contactGroups.items ?? []).map((contactGroup) => ({
        key: contactGroup.id.toString(),
        display: contactGroup.name,
      })),
      sorted: true,
      matchMode: {enabled: true},
    },
  ];

  return (
    <FilterDropdown
      defaultActiveCategoryId="services"
      categories={filterFields}
      appliedFilterSets={filterSets}
      onApply={onFilterSetChange}
      loading={loading}
    />
  );
};
